import { Controller } from "@hotwired/stimulus";
import Swiper from "swiper";
import { Navigation } from "swiper/modules";

/**
 * Content Slider Controller
 *
 * Handles the Swiper slider for the content-slider block.
 * Updates the content area when slides change.
 */
export default class extends Controller {
  static targets = ["container", "prevButton", "nextButton", "contentArea"];

  connect() {
    this.initSwiper();

    // Listen for hash changes (when clicking internal links)
    this.handleHashChange = this.handleHashChange.bind(this);
    window.addEventListener("hashchange", this.handleHashChange);
  }

  disconnect() {
    window.removeEventListener("hashchange", this.handleHashChange);

    if (this.swiper && this.containerTarget?.swiper === this.swiper) {
      this.swiper.destroy(true, true);
      delete this.containerTarget.swiper;
      this.swiper = null;
    }
  }

  /**
   * Handle hash change events (when clicking internal links)
   */
  handleHashChange() {
    if (!this.swiper || !window.location.hash) return;

    const hash = window.location.hash.slice(1);
    const slideIndex = Array.from(this.swiper.slides).findIndex(
      (slide) => slide.dataset.hash === hash
    );

    if (slideIndex !== -1) {
      this.swiper.slideTo(slideIndex, 300);
      this.updateContentArea();
    }
  }

  /**
   * Initialize Swiper instance
   */
  initSwiper() {
    if (!this.hasContainerTarget) return;

    // Prevent duplicate Swiper instances if the controller is registered more than once
    if (this.containerTarget.swiper) {
      this.swiper = this.containerTarget.swiper;
      this.updateNavigationVisibility();
      return;
    }

    // Check if any slides have a non-empty data-hash attribute
    const slides = this.containerTarget.querySelectorAll(".swiper-slide");
    const hasValidHashes = Array.from(slides).some(
      (slide) => slide.dataset.hash && slide.dataset.hash.trim() !== ""
    );

    // Count real slides (exclude spacer slides that are hidden on mobile)
    this.realSlideCount = Array.from(slides).filter(
      (slide) => !slide.classList.contains("content-slider-spacer")
    ).length;

    // Check if we're on mobile (< 768px)
    this.mobileMediaQuery = window.matchMedia("(max-width: 767px)");
    const isMobile = this.mobileMediaQuery.matches;

    const swiperConfig = {
      modules: [Navigation],
      slidesPerView: "auto",
      slidesPerGroup: 1,
      spaceBetween: 16,
      grabCursor: true,
      watchOverflow: true,
      navigation: {
        prevEl: this.hasPrevButtonTarget ? this.prevButtonTarget : null,
        nextEl: this.hasNextButtonTarget ? this.nextButtonTarget : null,
        disabledClass: "opacity-30 cursor-not-allowed",
      },
      breakpoints: {
        // Mobile: smaller gap
        0: {
          spaceBetween: 16,
        },
        // Tablet and up: larger gap
        768: {
          spaceBetween: 24,
        },
        1024: {
          spaceBetween: 40,
        },
      },
      on: {
        slideChange: () => {
          this.updateContentArea();
          // Manually update hash only for slides that have a valid data-hash
          if (hasValidHashes) {
            this.updateHash();
          }
          // Update navigation state for mobile
          this.updateMobileNavigation();
        },
        init: () => {
          // Set initial mobile navigation state
          this.updateMobileNavigation();
        },
      },
    };

    this.swiper = new Swiper(this.containerTarget, swiperConfig);

    // Keep a reference on the DOM element so we don't re-initialize
    this.containerTarget.swiper = this.swiper;
    this.updateNavigationVisibility();

    // Handle initial hash on page load (after Swiper is fully initialized)
    if (hasValidHashes && window.location.hash) {
      const hash = window.location.hash.slice(1);
      const slideIndex = Array.from(this.swiper.slides).findIndex(
        (slide) => slide.dataset.hash === hash
      );
      if (slideIndex !== -1) {
        // Use setTimeout to ensure Swiper is fully ready
        setTimeout(() => {
          this.swiper.slideTo(slideIndex, 0);
          this.updateContentArea();
        }, 0);
      }
    }
  }

  /**
   * Navigate to previous slide
   */
  prev() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  /**
   * Navigate to next slide
   */
  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  /**
   * Go to specific slide (called when clicking on non-active slide buttons)
   */
  goToSlide(event) {
    if (!this.swiper) return;

    // Find the slide that was clicked
    const slideElement = event.target.closest(".swiper-slide");
    if (!slideElement) return;

    const slides = Array.from(this.swiper.slides);
    const slideIndex = slides.indexOf(slideElement);

    if (slideIndex !== -1) {
      this.swiper.slideTo(slideIndex);
    }
  }

  /**
   * Update the content area with the active slide's content
   */
  updateContentArea() {
    if (!this.swiper || !this.hasContentAreaTarget) return;

    const activeSlide = this.swiper.slides[this.swiper.activeIndex];
    if (!activeSlide) return;

    const encodedContent = activeSlide.dataset.content;
    if (!encodedContent) return;

    try {
      // Decode the base64 content with proper UTF-8 handling
      const binaryString = atob(encodedContent);
      const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
      const decodedContent = new TextDecoder("utf-8").decode(bytes);
      this.contentAreaTarget.innerHTML = decodedContent;
    } catch (error) {
      console.error("Error decoding slide content:", error);
    }
  }

  /**
   * Update navigation button visibility based on slides
   */
  updateNavigationVisibility() {
    if (!this.swiper) return;

    const hasEnoughSlides = this.swiper.slides.length > 1;

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("hidden", !hasEnoughSlides);
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", !hasEnoughSlides);
    }
  }

  /**
   * Update navigation for mobile to prevent navigation to spacer slides
   */
  updateMobileNavigation() {
    if (!this.swiper || !this.mobileMediaQuery) return;

    const isMobile = this.mobileMediaQuery.matches;
    if (!isMobile) return;

    const isAtLastRealSlide = this.swiper.activeIndex >= this.realSlideCount - 1;

    // Disable next button and touch navigation when at last real slide on mobile
    if (this.hasNextButtonTarget) {
      const disabledClass = "opacity-30 cursor-not-allowed";
      if (isAtLastRealSlide) {
        this.nextButtonTarget.classList.add(...disabledClass.split(" "));
        this.nextButtonTarget.setAttribute("disabled", "");
      } else {
        this.nextButtonTarget.classList.remove(...disabledClass.split(" "));
        this.nextButtonTarget.removeAttribute("disabled");
      }
    }

    // Prevent swiping to spacer slides on mobile
    this.swiper.allowSlideNext = !isAtLastRealSlide;
  }

  /**
   * Update URL hash based on active slide (only if slide has a valid hash)
   */
  updateHash() {
    if (!this.swiper) return;

    const activeSlide = this.swiper.slides[this.swiper.activeIndex];
    if (!activeSlide) return;

    const hash = activeSlide.dataset.hash;

    if (hash && hash.trim() !== "") {
      // Update URL with hash without scrolling
      history.replaceState(null, null, `#${hash}`);
    } else {
      // Remove hash from URL if slide has no hash
      history.replaceState(
        null,
        null,
        window.location.pathname + window.location.search
      );
    }
  }
}
